/**
 * AriaPiani — Pagina selezione piano + checkout Stripe/PayPal + Founders Club.
 *
 * URL: /aria/piani/:mode  (mode = free | welcome | full | cinematic | lifetime)
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Sparkles, Check, CreditCard, Calendar, Crown, Flame } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const ACCENT = {
    free:      { c1: "#9EC3FF", c2: "#4F8BFF", glow: "rgba(158,195,255,0.32)" },
    welcome:   { c1: "#86EFAC", c2: "#10B981", glow: "rgba(134,239,172,0.4)" },
    full:      { c1: "#A855F7", c2: "#7C3AED", glow: "rgba(168,85,247,0.4)" },
    cinematic: { c1: "#FF1A5C", c2: "#A855F7", glow: "rgba(255,26,92,0.4)" },
    lifetime:  { c1: "#FFD66B", c2: "#FF6B3D", glow: "rgba(255,214,107,0.45)" },
};

// Linguaggio umano, niente tecnicismi
const FEATURES = {
    free: [
        "La bolla di chat in fondo a destra del tuo sito",
        "Aria risponde con intelligenza ai visitatori 24/7",
        "Compare la scritta 'Powered by TotalKlick' sotto la chat",
        "I dati delle conversazioni li vedi solo tu (TotalKlick)",
    ],
    welcome: [
        "Tutto quello che c'è in Free, più:",
        "Aria saluta da sola i visitatori appena arrivano",
        "Suggerisce le domande giuste con pulsanti pronti",
        "Vedi le conversazioni nella tua bacheca (in modo anonimo)",
        "In regola GDPR · 15 giorni gratis prima di pagare",
    ],
    full: [
        "Tutto quello che c'è in Welcome, più:",
        "Aria diventa parte del tuo sito — vive nello sfondo",
        "Si muove con lo scroll del visitatore, come se respirasse",
        "Conosce i tuoi prodotti, articoli, prezzi e ne parla",
        "Vedi nomi, email e contatti dei tuoi visitatori · 15 giorni gratis",
    ],
    cinematic: [
        "Tutto quello che c'è in Full, più:",
        "Aria ha un volto fatto di particelle che si muove mentre parla",
        "Parla con voce vera (non robotica) in italiano e altre lingue",
        "Mostra i tuoi prodotti come schede cliccabili dentro la chat",
        "Racconta ogni pagina del tuo sito come una guida personale",
    ],
};

export default function AriaPiani() {
    const { mode } = useParams();
    const [searchParams] = useSearchParams();
    const widgetToken = searchParams.get("widget_token") || null;
    const navigate = useNavigate();
    const { user } = useAuth();
    const [plans, setPlans] = useState([]);
    const [cycle, setCycle] = useState("monthly");
    const [loading, setLoading] = useState(true);
    const [checkoutLoading, setCheckoutLoading] = useState(null);  // "stripe:welcome" | "paypal:full" | "stripe:lifetime"
    const [counter, setCounter] = useState(null);
    const [showLifetime, setShowLifetime] = useState(mode === "lifetime");
    const [widgetSiteName, setWidgetSiteName] = useState(null);
    const [widgetActualPlan, setWidgetActualPlan] = useState(null);

    useEffect(() => {
        document.title = "Aria · Piani — TotalKlick";
        (async () => {
            try {
                const [pl, fc] = await Promise.all([
                    api.get("/billing/plans"),
                    api.get("/billing/founders/counter"),
                ]);
                setPlans(pl.data.plans || []);
                setCounter(fc.data);
            } finally { setLoading(false); }
        })();
    }, []);

    useEffect(() => {
        if (!widgetToken || !user) return;
        (async () => {
            try {
                const res = await api.get(`/widget/${widgetToken}/owner-config`);
                setWidgetSiteName(res.data?.site_name || null);
                setWidgetActualPlan(res.data?.plan_tier || "free");
            } catch (_e) { /* silent */ }
        })();
    }, [widgetToken, user]);

    const startCheckout = async (provider, planTier) => {
        if (!user) {
            const next = `/aria/piani/${planTier}${widgetToken ? `?widget_token=${widgetToken}` : ""}`;
            navigate(`/login?next=${encodeURIComponent(next)}`);
            return;
        }
        if (planTier === "free") {
            navigate(widgetToken ? `/aria-widget/config/${widgetToken}` : "/app/aria");
            return;
        }
        setCheckoutLoading(`${provider}:${planTier}`);
        try {
            const url = provider === "stripe" ? "/billing/stripe/checkout" : "/billing/paypal/checkout";
            const res = await api.post(url, {
                plan_tier: planTier,
                billing_cycle: cycle,
                origin_url: window.location.origin,
                widget_token: widgetToken || undefined,
            });
            if (res.data.checkout_url) {
                window.location.href = res.data.checkout_url;
            }
        } catch (e) {
            alert("Errore durante il checkout. Riprova tra un attimo.");
            setCheckoutLoading(null);
        }
    };

    const startLifetime = async (provider) => {
        if (!user) {
            navigate(`/login?next=${encodeURIComponent(`/aria/piani/lifetime`)}`);
            return;
        }
        setCheckoutLoading(`${provider}:lifetime`);
        try {
            const url = provider === "stripe" ? "/billing/stripe/lifetime" : "/billing/paypal/lifetime";
            const res = await api.post(url, { origin_url: window.location.origin });
            if (res.data.checkout_url) {
                window.location.href = res.data.checkout_url;
            }
        } catch (e) {
            const msg = e?.response?.data?.detail || "Errore durante il checkout Lifetime.";
            alert(msg);
            setCheckoutLoading(null);
        }
    };

    const paidPlans = useMemo(() => plans.filter(p => !p.free), [plans]);
    const freePlan = useMemo(() => plans.find(p => p.free), [plans]);

    return (
        <div className="relative min-h-screen overflow-x-hidden" style={{ background: "#05050F", color: "#fff", fontFamily: "Geist, Outfit, sans-serif" }} data-testid="aria-piani-page">
            {/* Ambient */}
            <div aria-hidden style={{
                position: "fixed", inset: 0, pointerEvents: "none",
                background: "radial-gradient(circle at 20% 10%, rgba(79,139,255,0.14) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(168,85,247,0.10) 0%, transparent 50%)",
            }} />

            <header className="relative pt-8 pb-6 px-4 sm:px-8 max-w-6xl mx-auto">
                <Link to={widgetToken ? `/aria-widget/config/${widgetToken}` : "/aria"} className="inline-flex items-center gap-1.5 text-[12px] text-white/65 hover:text-white transition uppercase tracking-[0.18em]" style={{ fontFamily: "JetBrains Mono, monospace" }} data-testid="back-to-aria">
                    <ArrowLeft size={12} /> {widgetToken ? "torna alla configurazione" : "torna alle modalità"}
                </Link>
                {widgetToken && (
                    <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11.5px] text-[#9EC3FF]" style={{ background: "rgba(79,139,255,0.08)", border: "1px solid rgba(79,139,255,0.25)", fontFamily: "Outfit, sans-serif" }} data-testid="piani-widget-context">
                        Upgrade per <b className="text-white">{widgetSiteName || "la tua istanza Aria"}</b>
                    </div>
                )}
            </header>

            <section className="relative px-4 sm:px-8 max-w-6xl mx-auto text-center pb-8">
                <div className="text-[10.5px] uppercase tracking-[0.32em] mb-3" style={{ color: "#9EC3FF", fontFamily: "JetBrains Mono, monospace" }}>
                    scegli il tuo piano
                </div>
                <h1 className="text-white" style={{ fontFamily: "Geist, sans-serif", fontWeight: 200, fontSize: "clamp(2rem, 5vw, 3.6rem)", letterSpacing: "-0.04em", lineHeight: 1.05 }}>
                    Inizia con <span style={{ background: "linear-gradient(180deg, #fff, #9EC3FF 70%, rgba(158,195,255,0.3))", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>15 giorni gratis</span>
                </h1>
                <p className="text-white/65 max-w-xl mx-auto mt-4 text-[14.5px]" style={{ fontFamily: "Outfit, sans-serif" }}>
                    Nessuna carta richiesta per i primi 15 giorni. Disdici quando vuoi. Se non confermi, torni gratis su Aria Free.
                </p>

                {/* Toggle mensile/annuale */}
                <div className="inline-flex items-center gap-1 mt-7 p-1 rounded-full border border-white/15 bg-white/5" data-testid="cycle-toggle">
                    {["monthly", "yearly"].map(c => (
                        <button
                            key={c}
                            type="button"
                            onClick={() => setCycle(c)}
                            className={`px-4 py-2 rounded-full text-[12.5px] transition ${cycle === c ? "text-[#05050F]" : "text-white/75"}`}
                            style={{
                                background: cycle === c ? "#fff" : "transparent",
                                fontFamily: "Geist, sans-serif", fontWeight: 500,
                            }}
                            data-testid={`cycle-${c}`}
                        >
                            {c === "monthly" ? "Mensile" : "Annuale"}
                            {c === "yearly" && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: "rgba(134,239,172,0.2)", color: "#86EFAC" }}>−17%</span>}
                        </button>
                    ))}
                </div>
            </section>

            {/* Founders Club Banner */}
            {counter && !counter.is_sold_out && (
                <FoundersBanner
                    counter={counter}
                    onClick={() => {
                        setShowLifetime(true);
                        setTimeout(() => {
                            document.getElementById("lifetime-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
                        }, 80);
                    }}
                />
            )}

            {/* GRIGLIA 4 PIANI */}
            <section className="relative px-4 sm:px-8 max-w-6xl mx-auto pb-12">
                {loading ? (
                    <div className="text-center text-white/50 py-20" data-testid="plans-loading">Caricamento piani…</div>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="plans-grid">
                        {/* Free card */}
                        {freePlan && (
                            <PlanCard
                                tier="free"
                                label={freePlan.label}
                                price={0}
                                billed="per sempre"
                                features={FEATURES.free}
                                isHighlight={mode === "free"}
                                highlightLabel={widgetToken ? (widgetActualPlan === "free" ? "il tuo piano" : "selezionato") : undefined}
                                cta="Resta su Free"
                                onCta={() => startCheckout("free", "free")}
                                ctaLoading={checkoutLoading === "free:free"}
                                disablePayPal
                            />
                        )}
                        {paidPlans.map((p) => {
                            const price = cycle === "monthly" ? p.monthly_eur : Math.round(p.yearly_eur / 12);
                            const billed = cycle === "monthly" ? "/mese" : "/mese (anno)";
                            return (
                                <PlanCard
                                    key={p.plan_tier}
                                    tier={p.plan_tier}
                                    label={p.label}
                                    price={price}
                                    billed={billed}
                                    yearlySavings={cycle === "yearly" ? p.yearly_savings_eur : 0}
                                    features={FEATURES[p.plan_tier] || []}
                                    isHighlight={mode === p.plan_tier}
                                    highlightLabel={widgetToken ? (widgetActualPlan === p.plan_tier ? "il tuo piano" : "selezionato") : undefined}
                                    cta="Inizia 15 giorni gratis"
                                    onCta={(provider) => startCheckout(provider, p.plan_tier)}
                                    ctaLoading={checkoutLoading?.endsWith(`:${p.plan_tier}`)}
                                />
                            );
                        })}
                    </div>
                )}
            </section>

            {/* SEZIONE LIFETIME */}
            {counter && !counter.is_sold_out && (
                <section id="lifetime-section" className="relative px-4 sm:px-8 max-w-5xl mx-auto pb-24">
                    <LifetimeCard
                        counter={counter}
                        onPayStripe={() => startLifetime("stripe")}
                        onPayPaypal={() => startLifetime("paypal")}
                        loadingStripe={checkoutLoading === "stripe:lifetime"}
                        loadingPaypal={checkoutLoading === "paypal:lifetime"}
                        defaultExpanded={showLifetime}
                    />
                </section>
            )}

            <footer className="relative px-4 sm:px-8 pb-16 max-w-4xl mx-auto text-center">
                <div className="text-[11px] text-white/35 uppercase tracking-[0.22em]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                    Pagamento sicuro · Stripe / PayPal · Disdici quando vuoi
                </div>
                <div className="mt-3 text-[11px] text-white/30" style={{ fontFamily: "Outfit, sans-serif" }}>
                    <Calendar size={11} className="inline mb-0.5 mr-1" />
                    I rinnovi sono automatici sia mensili che annuali. Ti avvisiamo via email prima di ogni addebito.
                </div>
            </footer>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
function FoundersBanner({ counter, onClick }) {
    const pct = Math.round(((counter.total_seats - counter.remaining) / counter.total_seats) * 100);
    return (
        <motion.button
            type="button"
            onClick={onClick}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="block max-w-5xl mx-auto mb-4 px-4 sm:px-8 w-full"
            data-testid="founders-banner"
        >
            <div className="relative overflow-hidden rounded-2xl px-5 py-3.5 sm:px-7 sm:py-4 text-left"
                style={{
                    background: "linear-gradient(120deg, rgba(255,107,61,0.18), rgba(255,214,107,0.12), rgba(255,107,61,0.18))",
                    border: "1px solid rgba(255,214,107,0.35)",
                    boxShadow: "0 12px 40px -16px rgba(255,107,61,0.35)",
                }}
            >
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
                    <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-xl grid place-items-center flex-none" style={{ background: "linear-gradient(135deg,#FFD66B,#FF6B3D)" }}>
                            <Crown size={16} color="#1a0c00" />
                        </div>
                        <div>
                            <div className="text-white text-[13px] sm:text-[14px]" style={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}>
                                Founders Club · Aria a vita
                            </div>
                            <div className="text-white/65 text-[11.5px]" style={{ fontFamily: "Outfit, sans-serif" }}>
                                Paga 1 volta · diritto futuro di acquistare quote al prezzo di lancio
                            </div>
                        </div>
                    </div>
                    <div className="flex-1 hidden sm:block" />
                    <div className="flex items-center gap-3">
                        <div className="text-right">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-white/55" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                                Posti rimasti
                            </div>
                            <div className="text-white text-[16px]" style={{ fontWeight: 600, fontFamily: "JetBrains Mono, monospace" }} data-testid="founders-remaining">
                                {counter.remaining}<span className="text-white/40">/{counter.total_seats}</span>
                            </div>
                        </div>
                        <div className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[12px]" style={{ background: "rgba(0,0,0,0.4)", color: "#FFD66B", border: "1px solid rgba(255,214,107,0.4)", fontFamily: "Geist, sans-serif", fontWeight: 500 }}>
                            Scopri <ArrowRight size={11} />
                        </div>
                    </div>
                </div>
                {/* progress */}
                <div className="mt-3 h-1 rounded-full bg-black/30 overflow-hidden">
                    <div className="h-full" style={{ width: `${Math.min(pct, 95)}%`, background: "linear-gradient(90deg,#FFD66B,#FF6B3D)" }} />
                </div>
            </div>
        </motion.button>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
function PlanCard({ tier, label, price, billed, yearlySavings = 0, features, isHighlight, highlightLabel, cta, onCta, ctaLoading, disablePayPal }) {
    const a = ACCENT[tier];
    return (
        <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="relative rounded-2xl p-6 flex flex-col"
            style={{
                background: "rgba(15,18,28,0.7)",
                border: `1px solid ${isHighlight ? a.c1 + "70" : "rgba(255,255,255,0.08)"}`,
                backdropFilter: "blur(20px)",
                boxShadow: isHighlight ? `0 30px 70px -20px ${a.glow}` : "none",
            }}
            data-testid={`plan-card-${tier}`}
        >
            {isHighlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] px-2.5 py-1 rounded-full uppercase tracking-[0.2em]" style={{ background: `linear-gradient(135deg, ${a.c1}, ${a.c2})`, fontFamily: "JetBrains Mono, monospace", color: "#fff", fontWeight: 600 }}>
                    {highlightLabel || "il tuo piano"}
                </div>
            )}
            <div className="w-10 h-10 rounded-xl grid place-items-center mb-4" style={{ background: `linear-gradient(135deg, ${a.c1}30, ${a.c2}10)`, border: `1px solid ${a.c1}40` }}>
                <Sparkles size={16} style={{ color: a.c1 }} />
            </div>
            <div className="text-white text-[18px] mb-1" style={{ fontWeight: 500 }}>{label}</div>
            <div className="flex items-baseline gap-1 mt-2 mb-1">
                <span className="text-white text-[36px]" style={{ fontWeight: 200, letterSpacing: "-0.025em" }}>€{price}</span>
                <span className="text-white/55 text-[12.5px]">{billed}</span>
            </div>
            {yearlySavings > 0 && (
                <div className="text-[11px]" style={{ color: a.c1, fontFamily: "JetBrains Mono, monospace" }}>
                    risparmia €{yearlySavings}/anno
                </div>
            )}
            <div className="my-5 h-px" style={{ background: "rgba(255,255,255,0.08)" }} />
            <ul className="space-y-2 mb-6 flex-1">
                {features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-[13px] text-white/80" style={{ fontFamily: "Outfit, sans-serif" }}>
                        <Check size={14} style={{ color: a.c1, marginTop: 2 }} className="flex-none" />
                        <span>{f}</span>
                    </li>
                ))}
            </ul>
            <div className="flex flex-col gap-2">
                <button
                    type="button"
                    onClick={() => onCta("stripe")}
                    disabled={ctaLoading}
                    className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-white text-[13.5px] transition hover:scale-[1.02] disabled:opacity-60"
                    style={{
                        background: `linear-gradient(135deg, ${a.c1}, ${a.c2})`,
                        fontFamily: "Geist, sans-serif", fontWeight: 500,
                        boxShadow: `0 18px 40px -14px ${a.glow}`,
                    }}
                    data-testid={`plan-cta-${tier}`}
                >
                    <CreditCard size={13} />
                    {ctaLoading ? "Apertura…" : cta}
                    <ArrowRight size={13} />
                </button>
                {!disablePayPal && (
                    <button
                        type="button"
                        onClick={() => onCta("paypal")}
                        disabled={ctaLoading}
                        className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-[12.5px] transition hover:bg-white/[0.06] disabled:opacity-60"
                        style={{
                            background: "transparent",
                            color: "#FFC439",
                            border: "1px solid rgba(255,196,57,0.45)",
                            fontFamily: "Geist, sans-serif", fontWeight: 500,
                        }}
                        data-testid={`plan-cta-paypal-${tier}`}
                    >
                        Paga con PayPal
                    </button>
                )}
                {tier === "free" && (
                    <div className="text-center text-[11px] text-white/40 mt-1" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                        gratis · per sempre
                    </div>
                )}
                {tier !== "free" && (
                    <div className="text-center text-[11px] text-white/40 mt-1" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                        nessuna carta richiesta
                    </div>
                )}
            </div>
        </motion.div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
function LifetimeCard({ counter, onPayStripe, onPayPaypal, loadingStripe, loadingPaypal, defaultExpanded }) {
    const [openInternal, setOpenInternal] = useState(false);
    const open = openInternal || !!defaultExpanded;
    const setOpen = (v) => setOpenInternal(typeof v === "function" ? v(open) : v);

    return (
        <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="relative rounded-3xl p-7 sm:p-9 overflow-hidden"
            style={{
                background: "linear-gradient(160deg, rgba(255,107,61,0.12), rgba(20,8,2,0.85))",
                border: "1px solid rgba(255,214,107,0.35)",
                backdropFilter: "blur(24px)",
                boxShadow: "0 40px 100px -20px rgba(255,107,61,0.3)",
            }}
            data-testid="lifetime-card"
        >
            {/* glow */}
            <div aria-hidden style={{ position: "absolute", inset: 0, pointerEvents: "none", background: "radial-gradient(circle at 80% 0%, rgba(255,214,107,0.18), transparent 50%)" }} />

            <div className="relative flex flex-col lg:flex-row gap-6 lg:gap-10">
                <div className="lg:w-2/3">
                    <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.28em] mb-3" style={{ color: "#FFD66B", fontFamily: "JetBrains Mono, monospace" }}>
                        <Flame size={11} /> Founders Club · 100 posti totali
                    </div>
                    <h2 className="text-white" style={{ fontFamily: "Geist, sans-serif", fontWeight: 200, fontSize: "clamp(1.7rem, 4vw, 2.6rem)", letterSpacing: "-0.03em", lineHeight: 1.05 }}>
                        Aria a vita <span style={{ background: "linear-gradient(135deg,#FFD66B,#FF6B3D)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>+ azionista al prezzo di lancio</span>
                    </h2>
                    <p className="text-white/75 mt-4 text-[14px]" style={{ fontFamily: "Outfit, sans-serif" }}>
                        Una sola volta. Aria Cinematic per sempre, senza più rinnovi. E quando TotalKlick aprirà un round d&apos;investimento, tu potrai comprare quote della società al <strong className="text-white">prezzo di oggi</strong> — anche se nel frattempo la valutazione sarà cresciuta 10× o 100×.
                    </p>

                    {!open ? (
                        <button type="button" onClick={() => setOpen(true)} className="mt-5 inline-flex items-center gap-1.5 text-[12.5px] text-white/85 hover:text-white" data-testid="lifetime-expand">
                            Cosa include esattamente? <ArrowRight size={12} />
                        </button>
                    ) : (
                        <ul className="mt-5 space-y-2.5" style={{ fontFamily: "Outfit, sans-serif" }}>
                            {[
                                ["Aria Cinematic a vita", "Il piano top per sempre, senza più rinnovi mensili o annuali"],
                                ["Badge numerato Founder #001-100", "Il tuo numero è tuo per sempre"],
                                ["Diritto di prelazione equity", "Quando apriremo un round, compri quote alla valutazione di oggi"],
                                ["Investor Updates mensili", "Newsletter privata con metriche reali e roadmap"],
                                ["Early access a tutte le feature", "Provi tutto in beta + voto consultivo sulla roadmap"],
                                ["Certificato PDF firmato", "Lo ricevi via email subito dopo il pagamento"],
                            ].map(([title, sub], i) => (
                                <li key={i} className="flex items-start gap-2.5">
                                    <Check size={14} style={{ color: "#FFD66B", marginTop: 3 }} className="flex-none" />
                                    <div>
                                        <div className="text-white text-[13.5px]" style={{ fontWeight: 500 }}>{title}</div>
                                        <div className="text-white/55 text-[12.5px]">{sub}</div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}

                    <div className="mt-5 text-[11px] text-white/45" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                        * Il diritto di prelazione è formalizzato con lettera d&apos;intenti firmata digitalmente all&apos;acquisto.
                    </div>
                </div>

                <div className="lg:w-1/3 flex flex-col justify-between gap-5">
                    <div className="rounded-2xl p-5" style={{ background: "rgba(0,0,0,0.4)", border: "1px solid rgba(255,214,107,0.25)" }}>
                        <div className="text-[10px] uppercase tracking-[0.28em] text-white/55" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                            Investimento una tantum
                        </div>
                        <div className="flex items-baseline gap-1.5 mt-2 mb-1">
                            <span className="text-white text-[44px]" style={{ fontWeight: 200, letterSpacing: "-0.03em" }}>€1.990</span>
                        </div>
                        <div className="text-[11.5px] text-white/55" style={{ fontFamily: "Outfit, sans-serif" }}>
                            Equivale a ~20 mesi di Cinematic. Recuperi il valore in meno di 2 anni — poi è solo guadagno.
                        </div>

                        <div className="my-4 h-px" style={{ background: "rgba(255,214,107,0.18)" }} />

                        <div className="flex items-center justify-between mb-1">
                            <div className="text-[11px] uppercase tracking-[0.22em] text-white/55" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                                Posti rimasti
                            </div>
                            <div className="text-[#FFD66B] text-[16px]" style={{ fontWeight: 600, fontFamily: "JetBrains Mono, monospace" }} data-testid="lifetime-remaining">
                                {counter.remaining}/{counter.total_seats}
                            </div>
                        </div>
                        <div className="h-1.5 rounded-full bg-black/40 overflow-hidden">
                            <div className="h-full" style={{ width: `${Math.min(((counter.total_seats - counter.remaining) / counter.total_seats) * 100, 97)}%`, background: "linear-gradient(90deg,#FFD66B,#FF6B3D)" }} />
                        </div>
                    </div>

                    <div className="flex flex-col gap-2.5">
                        <button
                            type="button"
                            onClick={onPayStripe}
                            disabled={loadingStripe}
                            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-[#1a0c00] text-[13.5px] transition hover:scale-[1.02] disabled:opacity-60"
                            style={{ background: "linear-gradient(135deg,#FFD66B,#FF6B3D)", fontFamily: "Geist, sans-serif", fontWeight: 600, boxShadow: "0 18px 40px -14px rgba(255,107,61,0.55)" }}
                            data-testid="lifetime-cta-stripe"
                        >
                            <CreditCard size={13} />
                            {loadingStripe ? "Apertura…" : "Diventa Founder · Carta"}
                            <ArrowRight size={13} />
                        </button>
                        <button
                            type="button"
                            onClick={onPayPaypal}
                            disabled={loadingPaypal}
                            className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-[12.5px] transition hover:bg-white/[0.06] disabled:opacity-60"
                            style={{ background: "transparent", color: "#FFC439", border: "1px solid rgba(255,196,57,0.55)", fontFamily: "Geist, sans-serif", fontWeight: 500 }}
                            data-testid="lifetime-cta-paypal"
                        >
                            {loadingPaypal ? "Apertura…" : "Diventa Founder · PayPal"}
                        </button>
                        <div className="text-center text-[10.5px] text-white/40 mt-1" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                            pagamento sicuro · certificato via email
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
}
